import { useEffect, useState } from 'react'
import { AtSign, CreditCard, Inbox, Mailbox, Phone } from 'lucide-react'
import type { DispatchHelpSessionOwner } from '@numeo/types'
import { fetchSessionOwner } from '../services/dispatchHelpApi'

interface Props {
    tabId: string
}

/**
 * Who the operator is talking to, shown once a thread is open.
 *
 * The list can only afford a one-line label, but an operator about to speak for a driver wants the
 * whole picture: the account they signed up with, their number, and every mailbox they can send from.
 * Loaded per thread rather than with the list so the list payload stays a keyset window.
 *
 * Renders nothing at all when the owner has none of it — an account can legitimately be a phone
 * number and nothing else, and an empty bordered box helps no one.
 */
export const SessionOwnerCard = ({ tabId }: Props) => {
    const [owner, setOwner] = useState<DispatchHelpSessionOwner | null>(null)

    useEffect(() => {
        let cancelled = false
        setOwner(null)
        // Fail-quiet: this is supplementary context, never a reason to break the thread view.
        fetchSessionOwner(tabId)
            .then((data) => !cancelled && setOwner(data))
            .catch(() => undefined)
        return () => {
            cancelled = true
        }
    }, [tabId])

    if (!owner) return null
    const { email, phone, connectedEmails, mailboxAddress, subscription } = owner
    if (!email && !phone && connectedEmails.length === 0 && !mailboxAddress && !subscription) return null

    return (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
            {email && <Field icon={<AtSign className="h-3.5 w-3.5" />} label="Account" value={email} />}
            {phone && <Field icon={<Phone className="h-3.5 w-3.5" />} label="Phone" value={phone} />}
            {connectedEmails.length > 0 && <Field icon={<Inbox className="h-3.5 w-3.5" />} label="Connected" value={connectedEmails.join(', ')} />}
            {mailboxAddress && <Field icon={<Mailbox className="h-3.5 w-3.5" />} label="Mailbox" value={mailboxAddress} />}
            {subscription && <Field icon={<CreditCard className="h-3.5 w-3.5" />} label="Plan" value={planLabel(subscription)} />}
        </div>
    )
}

/** "PRO · active", plus "· cancels <date>" when they have cancelled but the period has not lapsed. */
const planLabel = (sub: NonNullable<DispatchHelpSessionOwner['subscription']>): string => {
    const parts = [sub.plan, sub.status].filter(Boolean)
    if (sub.cancelAtPeriodEnd && sub.currentPeriodEnd) parts.push(`cancels ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`)
    return parts.join(' · ')
}

const Field = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
    <span className="flex min-w-0 items-center gap-1.5" title={`${label}: ${value}`}>
        <span className="shrink-0 text-slate-400 dark:text-slate-500">{icon}</span>
        <span className="shrink-0 text-slate-400 dark:text-slate-500">{label}</span>
        <span className="truncate font-medium text-slate-700 dark:text-slate-200">{value}</span>
    </span>
)
